/**
 * DateCell — DATE-variant editable cell. The on-the-wire value uses
 * MM/DD/YYYY but the native date input expects YYYY-MM-DD, so we hold the
 * ISO form internally and round-trip to slashes when committing.
 *
 * `setMaxMin` clamps the picker to today..today+6; today's order-service
 * actually accepts today+13 (banked Phase-6 backend-drift item).
 */

import { useEffect, useState } from 'react';

import dayjs from 'dayjs';

import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import {
  isoToSlashDate,
  slashToIsoDate,
} from '../../utils/cellValidation';

import CellShell from './CellShell';
import type { BaseCellProps } from './types';
import { commitInputChange } from './inputCellBehavior';

interface DateCellProps extends BaseCellProps {
  setMaxMin?: boolean;
}

const hydrateDate = (value: string | null): string => {
  if (!value || value === '-') return '';
  return value.includes('/') ? slashToIsoDate(value) : value;
};

const DateCell = ({
  value,
  id,
  name,
  isActive,
  isDisabled = false,
  isOptionalField = false,
  errorMessage,
  setData,
  setMaxMin = false,
}: DateCellProps) => {
  const [inputText, setInputText] = useState(() => hydrateDate(value));
  const today = dayjs();
  const maxDate = today.add(6, 'days');

  useEffect(() => {
    setInputText(hydrateDate(value));
  }, [value]);

  const handleBlur = (inputValue: string): void => {
    commitInputChange({
      inputValue,
      originalValue: value,
      id,
      name,
      isActive,
      errorMessage,
      isOptionalField,
      setData,
      normalizeOutput: isoToSlashDate,
      resolveCompareValue: (original) =>
        original?.includes('/') ? slashToIsoDate(original) : original,
    });
  };

  return (
    <CellShell
      isActive={isActive}
      isOptionalField={isOptionalField}
      errorMessage={errorMessage}
      value={value}
    >
      <Input
        id="input-value"
        type="date"
        aria-describedby="input-value"
        name="input"
        placeholder={value === '-' ? '-' : value === null ? '-' : undefined}
        disabled={isDisabled}
        value={inputText}
        className={cn(isActive && 'border-destructive')}
        onChange={(e) => setInputText(e.target.value)}
        onBlur={(e) => handleBlur(e.target.value)}
        min={setMaxMin ? today.format('YYYY-MM-DD') : undefined}
        max={setMaxMin ? maxDate.format('YYYY-MM-DD') : undefined}
        data-testid="input-value_id"
      />
    </CellShell>
  );
};

export default DateCell;
